import React, { PropsWithChildren, useMemo } from "react";

import { Stack } from "@mui/material";
import { orderBy } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { createSearchParams, useNavigate } from "react-router-dom-v5-compat";

import useGetFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { transitionTypeOptions } from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/common";
import { ComboboxOption } from "@src/sharedComponents/Combobox/Combobox";
import FormComboboxField from "@src/sharedComponents/Forms/FormComboboxField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import ModalWithClearButton from "@src/sharedComponents/Modal/ModalWithClearButton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./CreateTransitionModal.module.css";

interface TransitionTypeOption {
  label: string;
  value: string;
  typeId: number;
}

interface AdvisorOption {
  label: string;
  value: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: {
    transitionType: string;
    transitionTypeId: number | null;
    advisors: AdvisorOption[];
  }) => void;
}

type FormValues = {
  transitionType: TransitionTypeOption | null;
  advisors: ComboboxOption<string>[];
};

const CreateTransitionModal: React.FC<PropsWithChildren<Props>> = ({
  onClose,
  isOpen,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const auth = useAdvisorRequestAuth();
  const { data: employeesData } = useGetFartherEmployees("All", auth);

  const methods = useForm<FormValues>({
    defaultValues: {
      transitionType: null,
      advisors: [],
    },
  });

  const { transitionType, advisors } = methods.watch();
  const isFormValid = transitionType !== null && advisors.length > 0;

  const handleClearField = (field: keyof FormValues) => {
    methods.setValue(field, field === "transitionType" ? null : []);
  };

  const advisorsData = useMemo(() => {
    if (!employeesData?.advisors) {
      return [];
    }

    const sortedAdvisors = orderBy(
      employeesData.advisors,
      [(advisor) => advisor.name.last, (advisor) => advisor.name.first],
      ["asc", "asc"]
    );

    // Create unique display names by adding numbers for duplicates
    const nameCount = new Map<string, number>();
    return sortedAdvisors.map((advisor) => {
      const baseName = `${advisor.name.first} ${advisor.name.last}`;
      const count = nameCount.get(baseName) || 0;
      nameCount.set(baseName, count + 1);

      const displayName = count === 0 ? baseName : `${baseName} (${count + 1})`;

      return {
        label: displayName,
        value: advisor.advisorId,
      };
    });
  }, [employeesData]);

  const onContinue = () => {
    const values = methods.getValues();
    const advisors: AdvisorOption[] = values.advisors.map((advisor) => ({
      label: advisor.label,
      value: advisor.value || "",
    }));

    const formData = {
      transitionType: values.transitionType?.value || "",
      transitionTypeId: values.transitionType?.typeId || null,
      advisors,
    };

    onSubmit(formData);

    const urlParams = createSearchParams(location.search).toString();
    navigate(`/Advisor/Transitions/Create?${urlParams}`, {
      state: {
        type: "createTransition",
        data: formData,
      },
    });
  };

  return (
    <ModalWithClearButton closeModal={onClose} isOpen={isOpen}>
      <Text variant="title2" className={styles.header}>
        Create Transition
      </Text>

      <FormProvider {...methods}>
        <Text className={styles.label} variant="bodySmall" color={"textSubtle"}>
          Select a transition type and advisors to continue.
        </Text>

        <FormDropdownField
          name="transitionType"
          label="Type"
          values={transitionTypeOptions}
          getOptionKey={(option) => option.value}
          disablePortal={false}
          onClear={() => handleClearField("transitionType")}
        />
        <Spacer verticalSpacing="24px" />

        <FormComboboxField
          name="advisors"
          label="Advisors"
          resourceName="advisor"
          values={advisorsData}
        />

        <Spacer verticalSpacing="16px" />
      </FormProvider>

      <Stack
        spacing={2}
        direction="row"
        marginTop="24px"
        justifyContent="flex-end"
      >
        <Button
          text="Cancel"
          buttonType="primary"
          variant="text"
          disabled={false}
          onClick={onClose}
        />

        <Button
          text="Continue"
          buttonType="primary"
          variant="contained"
          disabled={!isFormValid}
          onClick={onContinue}
        />
      </Stack>
    </ModalWithClearButton>
  );
};

export default CreateTransitionModal;
